import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IColumnConfig, TABLE_COLUMNS } from '../../models/employee-details.model';
import { EmployeeData, EmployeeTrip } from '../../interfaces/employee-details.interface';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IChipsItem } from '../../interfaces/chips-item.interface';
import { MODAL_DATA_VAR, ModalRef } from 'mis-crystal-design-system/modal';
import { ASSETS_PATH_CONST } from '../../constants/image-assets.constants';
import { SVG_CONSTANTS } from '../../constants/svg.constants';
import { DateUtils } from 'src/app/utils/days-utils';

@Component({
  selector: 'app-employee-details',
  templateUrl: './employee-details.component.html',
  styleUrls: ['./employee-details.component.scss'],
})

export class EmployeeDetailsComponent implements OnInit {

  items: IChipsItem[] = [
    { label: 'Planned Sign in', value: 0, primaryColor: '#547F40', secondaryColor: '#F1FDF8', isSelected: false, isDisabled: false },
    { label: 'No Show', value: 0, primaryColor: '#B00020', secondaryColor: '#FAEFED', isSelected: false, isDisabled: false },
    { label: 'Adhoc', value: 0, primaryColor: '#ED711C', secondaryColor: '#FFEFC7', isSelected: false, isDisabled: false },
    { label: 'Yet to Board', value: 0, primaryColor: '#6A737D', secondaryColor: '#E0E0E0', isSelected: false, isDisabled: false },
  ];

  selectedChips: IChipsItem[] = [];
  employeeDetails: any[] = [];
  tripDetails: any;
  vehicleDetails: any[] = [];
  loading = false;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  dataSource = new MatTableDataSource<any>();
  columns: IColumnConfig[] = TABLE_COLUMNS.EMPLOYEE_DETAILS_COLUMNS;
  employeeData: EmployeeData[] = [];
  displayedColumns: string[] = this.columns.map((column) => column.matColumnDef);
  ASSETS_PATH_CONST = ASSETS_PATH_CONST;
  SVG_CONSTANTS = SVG_CONSTANTS;
  chipsLabelMap = new Map<string, string>();
  signInFields:string[] = ['plannedPickupTime','actualPickupTime']

  constructor(
    @Inject(MODAL_DATA_VAR) public data: any,
    private modalRef: ModalRef
  ) {}

  ngOnInit(): void {
    this.tripDetails = this.data['data'];
    this.transformEmployeeData();
    this.setVehicleDetails();

    this.chipsLabelMap.set('Adhoc', 'AD-HOC');
    this.chipsLabelMap.set('No Show', 'NO SHOW');
    this.chipsLabelMap.set('Planned Sign in', 'BOARDED');
    this.chipsLabelMap.set('Yet to Board', 'YET TO BOARD');
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onSelectionChange(selected: IChipsItem[]): void {
    this.selectedChips = selected;
    this.applyChipsFilter();
  }

  transformEmployeeData(): void {
    this.employeeData = this.data['data']['employees'].map((employee) => ({
      status: this.getEmployeeStatus(employee),
      employeeId: employee.employeeId,
      name: employee.name,
      phoneNumber: employee.phoneNumber,
      gender: employee.gender,
      plannedPickupLocation: employee.plannedPickupLocation?.address,
      actualPickupLocation: employee.actualPickupLocation?.address,
      plannedDropLocation: employee.plannedDropLocation?.address,
      actualDropLocation: employee.actualDropLocation?.address,
      plannedPickupTime: employee.plannedPickupTime,
      plannedDropTime: employee.plannedDropTime,
      actualPickupTime: employee.actualPickupTime,
      actualDropTime: employee.actualDropTime,
      driverReportingTime: employee.driverReportingTime,
      distanceTravelled: employee.distanceTravelled?.toFixed(2) + ' km',
      isAdhoc: employee.adhoc && this.updateCount('ADHOC'),
      isNoShow: employee.noShow && this.updateCount('NOSHOW'),
      isDropped: employee.dropped,
      isEscort: employee.escort,
      email: employee.email,
    }));
    if(this.data['data']['showUnknownRFID']){
      this.dataSource = new MatTableDataSource( this.data['data']['UnknownRfidData']);
    }
    else{
      this.dataSource = new MatTableDataSource(this.employeeData);
    }
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.dataSource.filterPredicate = (data:EmployeeData, filter:string)=> {
      const filters = filter.split(',').map((item) => item.trim())
      return filters.includes(data.status)
    }
  }

  updateCount(type: 'ADHOC' | 'NOSHOW' | 'YET TO BOARD' | 'BOARDED'): boolean {
    switch (type) {
      case 'ADHOC':
        this.items[2].value++;
        break;
      case 'NOSHOW':
        this.items[1].value++;
        break;
      case 'YET TO BOARD':
        this.items[3].value++;
        break;
      case 'BOARDED':
        this.items[0].value++;
        break;
    }
    return true;
  }

  getEmployeeStatus(employee: EmployeeTrip) {
    if (employee.noShow) {
      return 'NO SHOW';
    }
    if (employee.adhoc) {
      return 'AD-HOC';
    }
    return employee.actualPickupTime
      ? this.updateCount('BOARDED') && 'BOARDED'
      : this.updateCount('YET TO BOARD') && 'YET TO BOARD';
  }

  setVehicleDetails(): void {
    const vehicle = this.tripDetails?.vehicle;

    this.vehicleDetails = [
      { class: 'vehicle-registration-number', value: vehicle?.vehicleIdentification || 'N/A' },
      { class: 'vehicle-name', value: vehicle?.vehicleType?.name || 'N/A' },
      { class: 'vehicle-id', value: vehicle?.vehicleId || 'N/A' },
      { class: 'vehicle-type', value: vehicle?.vehicleType?.type ? `${vehicle.vehicleType.type} (${vehicle.vehicleType.maxCapacity})` : 'N/A' },
    ];
  }

  getVehicleImage() {
    return this.data['data'].routeSource === 'AUTO' ? ASSETS_PATH_CONST.CAB_ICON : ASSETS_PATH_CONST.BUS_ICON;
  }

  getOfficeAddress() {
    const tripData = this.data?.['data'];
    if (!tripData) return 'N/A';
    const plannedEndLocation = tripData.plannedEndLocation?.address;
    const plannedStartLocation = tripData.plannedStartLocation?.address;
    return tripData?.direction === 'IN' ? plannedEndLocation : plannedStartLocation;
  }

  closeModal() {
    this.modalRef.close();
  }

  applyChipsFilter() {
    if (this.selectedChips.length === 0) {
      this.dataSource.filter = '';
      return;
    }

    const filters = this.selectedChips.map(chip => this.chipsLabelMap.get(chip.label));

    // Join the selected filters with a comma for comparison in the filterPredicate
    this.dataSource.filter = filters.join(',');
  }

  getGenderIcon(gender:string){
    if(!gender){
      return '--'
    }
    return gender?.toLowerCase() === 'male' ? ASSETS_PATH_CONST.MALE_EMP_ICON : ASSETS_PATH_CONST.FEMALE_EMP_ICON
  }

  getSignInTimes(element:any,signInType:string){
    switch(signInType){
      case 'plannedPickupTime':
        return `${this.formatDate(element['plannedPickupTime'])} / ${this.formatDate(element['plannedDropTime'])}`
      case 'actualPickupTime':
        return `${this.formatDate(element['actualPickupTime'])} / ${this.formatDate(element['actualDropTime'])}`
      default:
        return ''
      
    }
  }

  formatDate(epoch:any){
    return epoch ? DateUtils.formatEpochToTime(epoch, 'HH:mm'): "--"
  }
  getShiftDetails(shiftValue:string){
    if( this.data['data']['tripType'] === 'Irregular' ) {
      const formattedShiftValue = DateUtils.formatDateTime(this.data['data']['actualStartTime'], 'ddd DD MMM, HH:mm');
       return formattedShiftValue
    }
    return DateUtils.formatDateTime(shiftValue, 'ddd DD MMM, HH:mm');
   }
}
